/**
 * Converte un colore hex in RGB.
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
	const cleanHex = hex.replace('#', '');
	return {
		r: parseInt(cleanHex.substring(0, 2), 16),
		g: parseInt(cleanHex.substring(2, 4), 16),
		b: parseInt(cleanHex.substring(4, 6), 16),
	};
};

/**
 * Converte RGB in hex.
 */
const rgbToHex = (r: number, g: number, b: number): string => {
	const toHex = (n: number) =>
		Math.round(Math.max(0, Math.min(255, n)))
			.toString(16)
			.padStart(2, '0');
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Converte un colore hex e un valore di opacità decimale in un colore hex con opacità.
 * @param hexColor - Colore in formato hex (es. '#303332' o '303332')
 * @param opacity - Valore di opacità tra 0 e 1 (es. 0.9)
 * @returns Colore hex con opacità (es. '#303332e6')
 */
export const hexWithOpacity = (hexColor: string, opacity: number): string => {
	const hex = hexColor.replace('#', '');
	const alpha = Math.round(opacity * 255)
		.toString(16)
		.padStart(2, '0');
	return `#${hex}${alpha}`;
};

/**
 * Mischia due colori in proporzione. Simile a color.mix() di SASS.
 * @param color1 - Primo colore in formato hex
 * @param color2 - Secondo colore in formato hex
 * @param weight - Peso del primo colore (0-100, default 50). 100 = tutto color1, 0 = tutto color2
 * @returns Colore mischato in formato hex
 */
export const mixColors = (
	color1: string,
	color2: string,
	weight: number = 50
): string => {
	const rgb1 = hexToRgb(color1);
	const rgb2 = hexToRgb(color2);
	const w = weight / 100;

	const r = rgb1.r * w + rgb2.r * (1 - w);
	const g = rgb1.g * w + rgb2.g * (1 - w);
	const b = rgb1.b * w + rgb2.b * (1 - w);

	return rgbToHex(r, g, b);
};

/**
 * Regola la luminosità di un colore. Simile a color.adjust() di SASS con $lightness.
 * @param hexColor - Colore in formato hex
 * @param lightness - Valore di luminosità da aggiungere (-100 a 100)
 * @returns Colore con luminosità regolata in formato hex
 */
export const adjustLightness = (
	hexColor: string,
	lightness: number
): string => {
	const rgb = hexToRgb(hexColor);
	const adjustment = (lightness / 100) * 255;

	return rgbToHex(rgb.r + adjustment, rgb.g + adjustment, rgb.b + adjustment);
};
